import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Shell } from './Shell'
import { LoginScreen } from './LoginScreen'
import { ContextSwitcher } from './ContextSwitcher'
import { MasterScreen } from '@/screens/MasterScreen'
import { MASTER_SCREENS } from '@/screens/registry'
import { DocumentScreen } from '@/screens/DocumentScreen'
import { DOCUMENT_SCREENS } from '@/screens/documents'
import { ApprovalScreen } from '@/screens/ApprovalScreen'
import { RightsScreen } from '@/screens/RightsScreen'
import { ItemRouteScreen } from '@/screens/ItemRouteScreen'
import { JobWorkScreen } from '@/screens/JobWorkScreen'
import { ItemRevisionScreen } from '@/screens/ItemRevisionScreen'
import { BomScreen } from '@/screens/BomScreen'
import { StoresDocScreen } from '@/screens/StoresDocScreen'
import { StockBalanceScreen } from '@/screens/StockBalanceScreen'
import { STORES_DOCS } from '@/screens/stores'
import { api, setSignOutHandler, setToken, hasStoredToken } from '@/lib/api'
import { isMockMode } from '@/lib/mock'
import { RightsProvider, useRights } from '@/lib/rights'
import { canView } from '@/screens/access'
import type { Session } from '@/types'

/** The idle rule from UI_TASTE.md. The browser signing itself out is a
 *  courtesy; the server enforces the same 30 minutes against last_seen_at,
 *  so a tab that fails to do this is still refused on its next request. */
const IDLE_MS = 30 * 60 * 1000
const IDLE_KEY = 'fkmt.erp.lastActive'   // shared across tabs

export function App() {
  const [session, setSession] = useState<Session | null>(null)
  // A refresh should not sign anybody out. The token survives in
  // sessionStorage; everything else is asked of the server, because company,
  // year and rights are its answers and not the browser's to remember.
  const [restoring, setRestoring] = useState(hasStoredToken())
  const [notice, setNotice] = useState<string | null>(null)
  const [switching, setSwitching] = useState(false)
  const [screen, setScreen] = useState(
    () => window.location.hash.slice(1) || 'admin/companies')

  const signOut = useCallback((reason: string) => {
    setSession(null); setToken(null); setNotice(reason)
  }, [])

  useEffect(() => {
    if (!hasStoredToken()) return
    void (async () => {
      try {
        const r = await api.get<{
          user: { user_id: string; name: string }
          companies: { company_id: string; name: string
                       years: { financial_year: string
                                status: 'Open' | 'Closed' }[] }[]
          session: { company_id: string; financial_year: string
                     expires_at: number }
        }>('/auth/session')
        const company = r.companies.find(
          c => c.company_id === r.session.company_id)
        const year = company?.years.find(
          y => y.financial_year === r.session.financial_year)
        setSession({
          userId: r.user.user_id,
          name: r.user.name,
          companyId: r.session.company_id,
          companyName: company?.name ?? r.session.company_id,
          financialYear: r.session.financial_year,
          yearStatus: year?.status ?? 'Open',
          availableCompanies: r.companies.map(
            c => ({ companyId: c.company_id, name: c.name })),
          availableYears: (company?.years ?? []).map(
            y => ({ financialYear: y.financial_year, status: y.status })),
          expiresAt: r.session.expires_at,
        })
      } catch {
        // Revoked, expired, or idled out while the tab was closed. The
        // person signs in again — which is the same outcome as before, just
        // not for the ordinary act of reloading a page.
        setToken(null)
      } finally { setRestoring(false) }
    })()
  }, [])

  useEffect(() => {
    setSignOutHandler(() =>
      signOut('Your session has ended. Please sign in again.'))
    const onHash = () =>
      setScreen(window.location.hash.slice(1) || 'admin/companies')
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [signOut])

  // Idle timeout, shared across tabs: working in one keeps the others alive,
  // and it is re-checked when a tab regains focus so a machine slept over
  // lunch signs out immediately rather than appearing signed in.
  useEffect(() => {
    if (!session) return
    const touch = () => localStorage.setItem(IDLE_KEY, String(Date.now()))
    touch()
    const check = () => {
      const last = Number(localStorage.getItem(IDLE_KEY) ?? Date.now())
      if (Date.now() - last > IDLE_MS) {
        signOut('Signed out after 30 minutes of inactivity. Please sign in again.')
      } else if (Date.now() > session.expiresAt) {
        signOut('Your session reached its 8-hour limit. Please sign in again.')
      }
    }
    const timer = window.setInterval(check, 30_000)
    const onFocus = () => check()
    for (const e of ['click', 'keydown', 'scroll'] as const) {
      window.addEventListener(e, touch, { passive: true })
    }
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(timer)
      for (const e of ['click', 'keydown', 'scroll'] as const) {
        window.removeEventListener(e, touch)
      }
      window.removeEventListener('focus', onFocus)
    }
  }, [session, signOut])

  if (!session) {
    return (
      <LoginScreen
        notice={notice}
        onSignedIn={(s, t) => {
          setToken(t); setSession(s); setNotice(null)
        }}
      />
    )
  }

  const navigate = (s: string) => { window.location.hash = s; setScreen(s) }

  // A blank moment beats the login form appearing and vanishing.
  if (restoring) {
    return <div className="boot">Restoring your session…</div>
  }

  const master = MASTER_SCREENS[screen]
  const document = DOCUMENT_SCREENS[screen]

  return (
    // Keyed on company and year: a switch starts a fresh rights list, because
    // a right in one company says nothing about another. RIGHTS_MODEL §6.1.
    <RightsProvider key={`${session.companyId}:${session.financialYear}`}>
    <RefreshRightsOn screen={screen} />
    <Shell
      session={session}
      screen={screen}
      onNavigate={navigate}
      onSwitchContext={() => setSwitching(true)}
      onSignOut={async () => {
        try { await api.create('/auth/logout', {}) } catch { /* going anyway */ }
        signOut('You have been signed out.')
      }}
    >
      {switching && (
        <ContextSwitcher
          session={session}
          onClose={() => setSwitching(false)}
          onSwitched={(companyId, companyName, financialYear, yearStatus) => {
            setSwitching(false)
            // Every screen re-reads on the next render — the company is part
            // of what the server scopes by, so nothing survives the switch.
            setSession({ ...session, companyId, companyName,
                         financialYear, yearStatus })
          }}
        />
      )}

      {isMockMode() && (
        <div className="banner banner-warning" style={{ marginBottom: 16 }}>
          <strong>Showing sample data.</strong> No backend is connected — live
          figures appear once the API is deployed. Records you add or edit here
          live in the browser and vanish on reload.
        </div>
      )}
      <ScreenGate screen={screen}>
      {screen === 'approvals' ? <ApprovalScreen />
        : screen === 'admin/rights' ? <RightsScreen />
        // `key` is not decoration. Without it React sees the same component
        // type across a navigation and REUSES the instance, keeping its
        // state - so Items rendered the rows still held from Item Groups,
        // under Items' columns. Keying on the screen forces a fresh mount,
        // which is the only honest thing when the subject changes entirely.
        // Item-first, so not a master screen: a route belongs to an item
        // and its operations are worked on together. JOB_WORK.md 4.
        : screen === 'masters/item-routes' ? <ItemRouteScreen key={screen} />
        // Its lifecycle is Draft → Issued → Received, driven by material
        // moving rather than by anyone approving, and it posts stock on both
        // legs. Not a document archetype. JOB_WORK.md.
        : screen === 'jobwork/order' ? <JobWorkScreen key={screen} />
        // A controlled document with an authorisation trail and no lines —
        // one revision concerns one item. Not a document archetype, which
        // carries a header AND its lines. ITEM_MASTER.md §11.6.1, ledger D26.
        : screen === 'design/item-revisions' ? <ItemRevisionScreen key={screen} />
        // Revisions of one item's parts list, released rather than edited.
        // Not a master (one row) and not a document (numbered, per year):
        // a BOM belongs to its item, for good. BOM.md, migration 046.
        : screen === 'masters/bom' ? <BomScreen key={screen} />
        // Goods Receipt, Issue and Transfer are ONE screen and three
        // declarations. They differ in a dozen fields and agree on the
        // lifecycle — and GHERP's nine receipt forms are what writing them
        // three times leads to. Ledger D28, STORES.md.
        : STORES_DOCS[screen] ? <StoresDocScreen key={screen} spec={STORES_DOCS[screen]} />
        : screen === 'stores/balance' ? <StockBalanceScreen key={screen} />
        : master ? <MasterScreen key={screen} spec={master} />
        : document ? <DocumentScreen key={screen} spec={document} />
        : <div className="empty">
            <p className="page-title">Not built yet</p>
            <p>“{screen}” is on the plan but has no screen yet.</p>
          </div>}
      </ScreenGate>
    </Shell>
    </RightsProvider>
  )
}

/** A screen the person may not view says so plainly — rather than opening and
 *  failing with "You do not have permission to do that." Everyone lands on
 *  Companies at sign-in, and most people may not view it. While the rights are
 *  still arriving the screen renders as usual; its own actions stay hidden
 *  until they are known. RIGHTS_MODEL.md 6.1. */
function ScreenGate({ screen, children }: { screen: string; children: ReactNode }) {
  const rights = useRights()
  if (rights.known && canView(screen, rights) === false) {
    return (
      <div className="empty">
        <p className="page-title">Not available to you</p>
        <p>You do not have access to this screen. Choose one from the menu —
           it lists only what you can open.</p>
      </div>
    )
  }
  return <>{children}</>
}

/** A grant shows the next time the user moves — FKMT's option A. */
function RefreshRightsOn({ screen }: { screen: string }) {
  const { refresh } = useRights()
  const first = useRef(true)
  useEffect(() => {
    // The provider has just loaded on mount; the first screen needs no
    // second request.
    if (first.current) { first.current = false; return }
    refresh()
  }, [screen, refresh])
  return null
}
